import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { routes } from '~/routes';
import { useIsReferralRebuildEnabled } from './useIsReferralRebuildEnabled';
import { useUserSelector } from './useUserSelector';

const REFERRAL_QUERY_PARAM = 'referral';
const SHORT_LINK_QUERY_PARAM = 'branch_url';

/**
 * The legacy (v1) referral link: `?referral=<mongo id>&branch_url=<short link>`.
 *
 * GOTU-7435: with the referral rebuild on this route is inert, exactly as
 * mobile's `openReferralV1` is — the two programmes must never both be live, or
 * one tap could be attributed twice. The stored legacy id is also cleared so a
 * value captured weeks ago can never be posted as a v2 code (GOTU-7432). The v2
 * link (`/referrals/v2/<CODE>`) is routed separately and is the only live
 * referral path while the flag is on.
 */
export const useReferralHandler = () => {
  const navigate = useNavigate();
  const user = useUserSelector();
  const { search } = useLocation();
  const isReferralRebuildEnabled = useIsReferralRebuildEnabled();

  useEffect(() => {
    if (isReferralRebuildEnabled) {
      localStorage.removeItem('referralId');
      return;
    }

    const qs = new URLSearchParams(search);
    const referralId = qs.get(REFERRAL_QUERY_PARAM);
    const shortLink = qs.get(SHORT_LINK_QUERY_PARAM);

    if (user && referralId) {
      navigate(routes.referral, { state: { shortLink } });
      localStorage.removeItem('referralId');
      return;
    }

    if (referralId) {
      localStorage.setItem('referralId', referralId);
    }
    // `navigate` is intentionally omitted: the effect reads `search` off the
    // render that carried the param and must not re-run for a navigate identity
    // change after the redirect has stripped the query string.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, search, isReferralRebuildEnabled]);

  const clearReferral = () => {
    localStorage.removeItem('referralId');
  };

  const getReferral = () => localStorage.getItem('referralId') || '';

  const handleReferral = (code) => {
    if (user || isReferralRebuildEnabled) {
      return;
    }

    localStorage.setItem('referralId', code);
  };

  return { clearReferral, getReferral, handleReferral };
};
